import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/lib/table';
import { Table } from '@hub/components/table/table';
import { useStore } from '@hub/helpers/use-store';
import { formatDate } from '@monorepo/client-common';
import { Flex, TextComponent } from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import { MidnightValue } from '@monorepo/types';

interface Props {
  accountId: string;
}

const AccountMidnightMarkTable: FunctionComponent<Props> = ({ accountId }) => {
  const {
    dataStore: { accountStore },
  } = useStore();

  const { isLoading, updateLoadingState } = useLoading();
  const [midnightValues, setMidnightValues] = useState<MidnightValue[]>([]);
  const { t } = useTranslation(['common', 'dashboardTemplates']);

  const columns: ColumnsType<MidnightValue> = [
    {
      title: t('common:date'),
      key: 'date',
      render: (_, record) =>
        formatDate(record.date, { template: 'mediumThin' }),
    },
    {
      title: t('dashboardTemplates:midnight_balance'),
      key: 'balance',
      render: (_, record) => record.balance,
    },
    {
      title: t('dashboardTemplates:midnight_equity'),
      key: 'equity',
      render: (_, record) => record.equity,
    },
  ];

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        const result = await accountStore.getMidnightHistory(accountId);
        setMidnightValues(result);
      } catch (e) {
        console.error('Failed getting midnight values', e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId]);

  return (
    <Flex className="account-midnight-mark__table" vertical gap={5}>
      <TextComponent>
        {t('dashboardTemplates:midnight_values_last_60_days')}
      </TextComponent>
      <Table
        rowHoverable={false}
        size="small"
        loading={isLoading}
        rowKey={(record: MidnightValue) => record._id}
        bordered={false}
        columns={columns}
        dataSource={midnightValues}
      />
    </Flex>
  );
};

export default AccountMidnightMarkTable;
